import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Chip, Button, MenuItem, TextField, CircularProgress,
  Snackbar, Alert, DialogTitle, DialogContent, DialogActions, Table, TableHead,
  TableRow, TableCell, TableBody, Divider, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import apiService from '../services/api.service';

interface OrderRow {
  salesorderid: number; sonumber: string; cardcode: string; cardname: string; currency?: string;
  status: string; docdate?: string; totalnetamount: number; hasbelowfloorline: boolean;
  creditcheckstatus?: string; sapdocnum?: string; ponumber?: string; createddate: string;
}

interface CustomerOpt { cardCode: string; cardName: string }

const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  Draft: 'default', PendingApproval: 'warning', Approved: 'success', Rejected: 'error', Failed: 'error', Cancelled: 'default',
};
const STATUSES = ['', 'Draft', 'PendingApproval', 'Approved', 'Rejected', 'Failed', 'Cancelled'];
const fmt = (n: number) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '—');

function SalesOrderList() {
  const navigate = useNavigate();
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [status, setStatus] = useState('');
  const [customer, setCustomer] = useState<CustomerOpt | null>(null);
  const [custOptions, setCustOptions] = useState<CustomerOpt[]>([]);
  const [custSearch, setCustSearch] = useState('');
  const [custLoading, setCustLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const notify = useCallback((msg: string, sev: 'success' | 'error' = 'error') => setToast({ msg, sev }), []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res: any = await apiService.listSalesOrders(companyId, status || undefined, customer?.cardCode || undefined);
      setRows(res.data || []);
    } catch (e: any) { notify(e.response?.data?.message || 'Failed to load orders'); }
    finally { setLoading(false); }
  }, [companyId, status, customer, notify]);

  useEffect(() => { void load(); }, [load]);

  // Debounced customer lookup for the filter. Only customers this user may see
  // are returned. A view-only user without the create right gets 403 here —
  // leave the list empty rather than showing an error for an optional filter.
  useEffect(() => {
    if (!companyId) return;
    const handle = setTimeout(async () => {
      setCustLoading(true);
      try {
        const res: any = await apiService.getOrderCustomers(companyId, custSearch || undefined);
        setCustOptions(res.data || []);
      } catch { setCustOptions([]); }
      finally { setCustLoading(false); }
    }, 350);
    return () => clearTimeout(handle);
  }, [companyId, custSearch]);

  const openDetail = async (orderId: number) => {
    try { const res: any = await apiService.getSalesOrder(orderId); setDetail(res.data); }
    catch (e: any) { notify(e.response?.data?.message || 'Failed to load order'); }
  };

  const submit = async (orderId: number) => {
    try {
      await apiService.submitSalesOrder(orderId);
      notify('Submitted for approval', 'success');
      setDetail(null); await load();
    } catch (e: any) {
      if (e.response?.status === 422) notify((e.response.data?.errors || ['Validation failed']).join('; '));
      else notify(e.response?.data?.message || 'Submit failed');
    }
  };

  if (!companyId) {
    return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="info">Select a company (top bar) to view sales orders.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Typography variant="h5" component="h1">My Sales Orders</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Autocomplete
            sx={{ minWidth: 240 }} size="small"
            options={custOptions}
            value={customer}
            onChange={(_, v) => setCustomer(v)}
            onInputChange={(_, v, reason) => { if (reason === 'input') setCustSearch(v); }}
            filterOptions={(x) => x}
            getOptionLabel={(o) => `${o.cardName} (${o.cardCode})`}
            isOptionEqualToValue={(a, b) => a.cardCode === b.cardCode}
            loading={custLoading}
            noOptionsText={custSearch ? 'No matching customers' : 'Type to search customers'}
            renderInput={(params) => (
              <TextField {...params} label="Customer" InputProps={{
                ...params.InputProps,
                endAdornment: (<>{custLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>),
              }} />
            )}
          />
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
            {STATUSES.map((s) => <MenuItem key={s || 'all'} value={s}>{s || 'All'}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')}>New Order</Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <ResponsiveTable
          ariaLabel="Sales orders"
          rows={rows}
          getRowKey={(r) => r.salesorderid}
          onRowClick={(r) => void openDetail(r.salesorderid)}
          emptyMessage="No sales orders yet."
          columns={[
            { id: 'sonumber', header: 'SO #', cell: (r) => r.sonumber },
            { id: 'cardname', header: 'Customer', cell: (r) => `${r.cardname} (${r.cardcode})` },
            { id: 'docdate', header: 'Date', cell: (r) => fmtDate(r.docdate || r.createddate) },
            { id: 'status', header: 'Status', cell: (r) => <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'} /> },
            { id: 'total', header: 'Total', align: 'right', cell: (r) => fmt(r.totalnetamount) },
            { id: 'flags', header: '', cell: (r) => (<Stack direction="row" spacing={0.5}>
              {r.hasbelowfloorline && <Chip size="small" color="error" label="below floor" />}
              {r.sapdocnum && <Chip size="small" color="success" label={`SAP ${r.sapdocnum}`} />}
            </Stack>) },
          ]}
        />
      )}

      <ResponsiveDialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle>
              {detail.sonumber} — {detail.cardname}
              <Chip size="small" sx={{ ml: 1 }} label={detail.status} color={STATUS_COLOR[detail.status] || 'default'} />
              {detail.ponumber && (
                <Typography variant="body2" color="text.secondary">PO Number: {detail.ponumber}</Typography>
              )}
            </DialogTitle>
            <DialogContent dividers>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Item</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">List</TableCell>
                  <TableCell align="right">Floor</TableCell><TableCell align="right">Disc %</TableCell>
                  <TableCell align="right">Unit</TableCell><TableCell align="right">Total</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(detail.lines || []).map((l: any) => (
                    <TableRow key={l.lineid}>
                      <TableCell>{l.itemcode}{l.isbelowfloor && <Chip size="small" color="error" label="floor" sx={{ ml: 0.5 }} />}</TableCell>
                      <TableCell align="right">{Number(l.quantity)}</TableCell>
                      <TableCell align="right">{l.listprice != null ? fmt(Number(l.listprice)) : '—'}</TableCell>
                      <TableCell align="right">{l.floorprice != null ? fmt(Number(l.floorprice)) : '—'}</TableCell>
                      <TableCell align="right">{Number(l.discountpercent || 0).toFixed(2)}%</TableCell>
                      <TableCell align="right">{fmt(Number(l.unitprice))}</TableCell>
                      <TableCell align="right">{fmt(Number(l.linetotal))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Typography variant="subtitle1">Total: {fmt(Number(detail.totalnetamount))} {detail.currency || ''}</Typography>
              </Stack>
              {(detail.history || []).length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>History</Typography>
                  {(detail.history || []).map((h: any) => (
                    <Typography key={h.approvalid} variant="caption" display="block" color="text.secondary">
                      {new Date(h.actiondate).toLocaleString()} — {h.action}{h.comments ? `: ${h.comments}` : ''}
                    </Typography>
                  ))}
                </>
              )}
            </DialogContent>
            <DialogActions>
              {detail.status === 'Draft' && (
                <>
                  <Button onClick={() => navigate(`/sales/orders/${detail.salesorderid}/edit`)}>Edit</Button>
                  <Button variant="contained" onClick={() => void submit(detail.salesorderid)}>Submit for Approval</Button>
                </>
              )}
              <Button onClick={() => setDetail(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </ResponsiveDialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default SalesOrderList;
